// Drugs' Molecule section (features/molecule.ts): the Receptor structure switch and the structure's facts in the panel,
// the driver that draws and clears the meshes with the scene, and the Methods block. The driver is mounted by the app, not
// the panel, so a closed phone sheet keeps the molecule in step with the switch.

import { useEffect } from 'react';
import { useAtlas } from '../app/store.ts';
import { openedSlot } from '../app/sims.ts';
import { presetCamera } from '../app/framing.ts';
import { scenarioById } from '../scenarios/index.ts';
import { getViewport } from '../viewport/host.ts';
import { lastCamera } from '../viewport/events.ts';
import { Section, Switch } from '../ui/primitives.tsx';
import type { LiveViewport } from './stim.ts';
import { TARGET_LABEL } from './Traces.tsx';
import { build, citeShort, clearMolecule, frameOrbit, loadManifest, loadStructure, LOOK, methodLabel, MM_PER_ANGSTROM, showMolecule, STRUCTURES_DIR, structureFor, useMolecule, viewportDrawsMolecule, type MoleculeState, type StructureRecord } from './molecule.ts';

/** The camera back to the scene's own view (the switch off within Drugs). */
function restoreCamera() {
  const s = useAtlas.getState();
  const sc = scenarioById(s.scenario);
  const p = sc.presets.find((x) => x.id === s.preset) ?? sc.presets[0];
  if (p) s.camera({ ...presetCamera(s, p), transitionMs: 600 });
}

/** The camera to the molecule, from the front and a little above, so it stands clear of the brain beside it (from the hero's
 *  front-left it would sit between the camera and the left temporal lobe). The view's own field of view and aspect. */
export const MOLECULE_VIEW = { yawDeg: 0, pitchDeg: 12 };
function flyToMolecule(centreMm: [number, number, number], sizeMm: [number, number, number]) {
  const last = lastCamera.current;
  const cam = { ...MOLECULE_VIEW, fovYDeg: last?.fovYDeg ?? 28, aspect: last && last.height ? last.width / last.height : 1.5 };
  useAtlas.getState().camera({ orbit: frameOrbit(centreMm, sizeMm, cam), transitionMs: 700 });
}

/** Draws the run's structure while Drugs is on screen with the switch on and the brain in; takes it away otherwise. */
export function MoleculeDriver() {
  const scenario = useAtlas((s) => s.scenario);
  const wanted = useAtlas((s) => !!s.toggles.molecule);
  const loaded = useAtlas((s) => s.load.status === 'ready');
  const run = useAtlas((s) => (s.sims.scenario === s.scenario && s.sims.status === 'ready' ? (s.sims.slots.find((x) => x.slot === 'main' && x.format === 'pharma')?.path ?? null) : null));
  const active = scenario === 'drugs' && wanted && loaded && !!run;
  useEffect(() => {
    let on = true;
    const set = (p: Partial<Omit<MoleculeState, 'set'>>) => {
      if (on) useMolecule.getState().set(p);
    };
    if (!active) {
      const prev = useMolecule.getState();
      if (prev.status === 'off' && !prev.drawn.length) return;
      void (async () => {
        const vp = (await getViewport()) as LiveViewport | null;
        if (prev.drawn.length && vp) await clearMolecule(vp, prev.drawn);
        if (!on) return;
        useMolecule.getState().set({ status: 'off', run: null, entry: null, file: null, why: null, drawn: [], frame: null, counts: null, flew: false });
        const s = useAtlas.getState();
        if (prev.flew && s.scenario === 'drugs' && !s.toggles.molecule) restoreCamera();
      })();
      return () => {
        on = false;
      };
    }
    const runId = run!.split('/').pop()!;
    set({ status: 'loading', run: runId, why: null });
    void (async () => {
      try {
        const manifest = await loadManifest();
        if (!on) return;
        if (!manifest) {
          set({ status: 'none', entry: null, file: null, why: `The receptor structures (sims/${STRUCTURES_DIR}) aren’t on this server yet.` });
          return;
        }
        const o = openedSlot('main');
        const primary = o?.format === 'pharma' ? (o.sim.primaryTarget ?? null) : null;
        const entry = structureFor(runId, primary, manifest);
        if (!entry) {
          set({ status: 'none', entry: null, file: null, why: 'No structure in the set serves this run.' });
          return;
        }
        const file = await loadStructure(entry);
        if (!on) return;
        const vp = (await getViewport()) as LiveViewport | null;
        if (!on) return;
        if (!viewportDrawsMolecule(vp)) {
          set({ status: 'nomesh', entry, file, why: 'The 2D preview draws no molecule: it needs the 3D viewport.' });
          return;
        }
        const built = build(file, MM_PER_ANGSTROM, entry.uniprot);
        const stale = useMolecule.getState().drawn.filter((id) => !built.meshes.some((m) => m.id === id));
        if (stale.length) await clearMolecule(vp!, stale);
        const drawn = await showMolecule(vp!, built);
        if (!on) {
          await clearMolecule(vp!, drawn);
          return;
        }
        set({ status: 'on', entry, file, drawn, frame: { centreMm: built.centreMm, sizeMm: built.sizeMm }, counts: built.counts, flew: true, why: null });
        flyToMolecule(built.centreMm, built.sizeMm);
      } catch (err) {
        set({ status: 'error', why: err instanceof Error ? err.message : String(err) });
      }
    })();
    return () => {
      on = false;
    };
  }, [active, run]);
  return null;
}

const LEGEND: { key: keyof typeof LOOK; label: string }[] = [
  { key: 'helix', label: 'Helix' },
  { key: 'strand', label: 'Strand' },
  { key: 'coil', label: 'Coil' },
  { key: 'drug', label: 'Drug' },
  { key: 'other', label: 'Other chains' },
];

/** What is in the pocket and how it is drawn, for the panel. */
export function ligandLine(entry: StructureRecord): string {
  const l = entry.ligand;
  const bound = l.matches_drug ? `${l.name} bound` : `${l.name} bound in place of the run’s drug`;
  const copies = l.copies > 1 ? ` (${l.copies} copies)` : '';
  return `${bound}${copies}; ${TARGET_LABEL(entry.target)} coloured by secondary structure, fusions and other chains dimmed.`;
}

export function MoleculeSection() {
  const on = useAtlas((s) => !!s.toggles.molecule);
  const setToggle = useAtlas((s) => s.setToggle);
  const status = useMolecule((s) => s.status);
  const entry = useMolecule((s) => s.entry);
  const why = useMolecule((s) => s.why);
  const cite = entry ? citeShort(entry) : null;
  return (
    <Section title="Molecule">
      <label className="row">
        <span>Receptor structure</span>
        <Switch checked={on} onChange={(v) => setToggle('molecule', v)} label="Receptor structure" />
      </label>
      {!on ? (
        <p className="live-hint molecule-hint">The run’s target as a measured structure from the PDB, standing beside the brain.</p>
      ) : (
        <div className="molecule" data-status={status}>
          {entry ? (
            <>
              <p className="molecule-title">{entry.title}</p>
              <p className="molecule-ref muted">
                {'PDB '}
                <a href={entry.pdb_doi} target="_blank" rel="noreferrer">
                  {entry.id}
                </a>
                {` · ${methodLabel(entry.method)}${entry.resolution_a ? `, ${entry.resolution_a} Å` : ''}${cite ? ` · ${cite}` : ''}`}
              </p>
              <p className="molecule-ligand">{ligandLine(entry)}</p>
              <ul className="molecule-legend" aria-label="Colours">
                {LEGEND.map((l) => (
                  <li key={l.key} style={{ ['--sw' as string]: LOOK[l.key] }}>
                    {l.label}
                  </li>
                ))}
              </ul>
              {entry.note ? <p className="live-hint molecule-note">{entry.note}</p> : null}
            </>
          ) : null}
          {status === 'loading' ? <p className="live-hint">Reading the structure…</p> : null}
          {status === 'on' ? <p className="live-hint molecule-scale">{`1 Å drawn as ${MM_PER_ANGSTROM} mm, five million times life size, left of the brain.`}</p> : null}
          {status === 'none' || status === 'nomesh' || status === 'error' ? <p className="live-hint molecule-why">{why}</p> : null}
        </div>
      )}
    </Section>
  );
}

/** The Methods sheet's Molecule block, while a structure is on. */
export function MoleculeMethods() {
  const entry = useMolecule((s) => s.entry);
  const file = useMolecule((s) => s.file);
  const status = useMolecule((s) => s.status);
  const counts = useMolecule((s) => s.counts);
  if (!entry || status === 'off' || status === 'loading') return null;
  const otherChains = file ? file.chains.filter((c) => !c.target) : [];
  const entities = [...new Set(otherChains.map((c) => c.entity).filter((e): e is string => !!e))];
  const drug = file?.ligands.find((l) => l.role === 'drug');
  const co = file ? [...new Set(file.ligands.filter((l) => l.role !== 'drug').map((l) => l.name ?? l.ccd))] : [];
  const doi = entry.citation?.pdbx_database_id_DOI ?? file?.citation.doi ?? null;
  const cite = citeShort(entry);
  return (
    <section className="methods-sim methods-molecule">
      <h5 className="caps">Molecule</h5>
      <dl>
        <dt>Structure</dt>
        <dd>
          {`PDB ${entry.id}: ${entry.title} (${methodLabel(entry.method)}${entry.resolution_a ? `, ${entry.resolution_a} Å` : ''}). `}
          <Link href={entry.pdb_doi} />
        </dd>
        {cite || doi ? (
          <>
            <dt>Citation</dt>
            <dd>
              {cite ? `${cite}${entry.citation?.rcsb_journal_abbrev ? `, ${entry.citation.rcsb_journal_abbrev}` : ''}. ` : ''}
              {doi ? <Link href={`https://doi.org/${doi}`} /> : null}
            </dd>
          </>
        ) : null}
        <dt>Drawn</dt>
        <dd>
          {`The ${TARGET_LABEL(entry.target)} chains’ Cα trace (${counts ? counts.ca.toLocaleString('en-GB') : '…'} Cα over all chains) as tubes coloured by secondary structure (helix, strand, coil), split where residues are unmodelled`}
          {counts?.dimmed ? `; the construct’s ${counts.dimmed} fusion residues inside the target chain dim` : ''}
          {otherChains.length ? `; ${otherChains.length} other chain${otherChains.length > 1 ? 's' : ''}${entities.length ? ` (${entities.join('; ')})` : ''} as one dim tube` : ''}
          {drug ? `; ${entry.ligand.name}’s ${drug.atoms.length} heavy atoms with distance bonds` : ''}
          {co.length ? `; co-ligands ${co.join(', ')}` : ''}.{entry.ligand.matches_drug ? '' : ` ${entry.ligand.name} stands in: no structure holds the run’s drug.`}
        </dd>
        <dt>Scale and place</dt>
        <dd>{`1 Å drawn as ${MM_PER_ANGSTROM} mm, five million times life size, standing on its longest axis ${GAP_TEXT} left of the brain. The placement is illustrative; the coordinates are the entry’s own.`}</dd>
        <dt>Source</dt>
        <dd>
          {'RCSB PDB (CC0), compacted by pipelines/pharma-structures to '}
          <span className="mono">{`sims/${STRUCTURES_DIR}/${entry.path}`}</span>
          {' · reference'}
        </dd>
        {entry.note ? (
          <>
            <dt>Note</dt>
            <dd>{entry.note}</dd>
          </>
        ) : null}
      </dl>
    </section>
  );
}
const GAP_TEXT = '18 mm';

/** A source as its own link. */
function Link({ href }: { href: string }) {
  return (
    <a href={href} target="_blank" rel="noreferrer">
      {href}
    </a>
  );
}
